import { AMOUNT_QUOTE_DEBOUNCE_MS } from "../../utils/amountQuote";
import { useCallback, useEffect, useRef, useState } from "react";
import { withDeadline } from "../../utils/withDeadline";
import {
  formatSolanaTokenAmount,
  type SolanaTokenInfo,
} from "../../config/solana";
import {
  getSolanaBalance,
  getSolanaTokenBalance,
  type SolanaRpc,
} from "../../joinsplit-v2/solana/rpc";
import {
  estimateSolanaDepositFees,
  type SolanaDepositFees,
} from "../../joinsplit-v2/solana/depositFees";

export interface SolanaDepositQuote extends SolanaDepositFees {
  balance: bigint;
  solBalance: bigint;
  max: bigint;
}

export function useSolanaDepositQuote({
  rpc,
  account,
  token,
  enabled,
  depositMergeEnabled = false,
  onchainRecoveryEnabled = false,
  networkKey = "",
  setAmount,
}: {
  rpc: SolanaRpc;
  account: string;
  token: SolanaTokenInfo;
  enabled: boolean;
  depositMergeEnabled?: boolean;
  onchainRecoveryEnabled?: boolean;
  networkKey?: string;
  setAmount: (value: string) => void;
}) {
  const key = `${networkKey}:${account}:${token.mint ?? "sol"}:${token.tokenProgram}:${depositMergeEnabled}:${onchainRecoveryEnabled}`;
  const generation = useRef(0);
  const lastQuote = useRef<{ key: string; quote: SolanaDepositQuote } | null>(
    null,
  );
  const rpcRef = useRef(rpc);
  rpcRef.current = rpc;
  const amountEdit = useRef(0);
  const request = useRef<AbortController | null>(null);
  const editTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [state, setState] = useState<{
    key: string;
    quote: SolanaDepositQuote | null;
    loading: boolean;
    error: string | null;
  } | null>(null);
  const load = useCallback(async () => {
    if (!enabled || !account) return null;
    if (editTimer.current) clearTimeout(editTimer.current);
    editTimer.current = null;
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    const id = ++generation.current;
    setState((previous) => ({
      key,
      quote: previous?.key === key ? previous.quote : null,
      loading: true,
      error: null,
    }));
    try {
      const [solBalance, tokenBalance, fees] = await withDeadline(
        () =>
          Promise.all([
            getSolanaBalance(rpcRef.current, account),
            token.mint
              ? getSolanaTokenBalance(
                  rpcRef.current,
                  account,
                  token.mint,
                  token.tokenProgram,
                )
              : Promise.resolve(null),
            estimateSolanaDepositFees(rpcRef.current, account, {
              depositMerge: depositMergeEnabled,
              onchainRecovery: onchainRecoveryEnabled,
            }),
          ]),
        "Could not estimate network fees. Select Max to retry.",
        controller.signal,
      );
      if (id !== generation.current) return null;
      const balance = tokenBalance ?? solBalance;
      const max =
        solBalance < fees.reserve
          ? 0n
          : token.mint
            ? balance
            : balance - fees.reserve;
      const quote = { ...fees, balance, solBalance, max };
      lastQuote.current = { key, quote };
      setState({ key, quote, loading: false, error: null });
      return quote;
    } catch {
      if (id === generation.current)
        setState({
          key,
          quote: null,
          loading: false,
          error: "Could not estimate network fees. Select Max to retry.",
        });
      return null;
    } finally {
      if (request.current === controller) request.current = null;
    }
  }, [
    account,
    token.mint,
    token.tokenProgram,
    enabled,
    depositMergeEnabled,
    onchainRecoveryEnabled,
    key,
  ]);
  useEffect(() => {
    void load();
    const timer = enabled
      ? window.setInterval(() => {
          if (!editTimer.current && !request.current) void load();
        }, 60_000)
      : undefined;
    return () => {
      ++amountEdit.current;
      ++generation.current;
      if (editTimer.current) clearTimeout(editTimer.current);
      request.current?.abort();
      request.current = null;
      window.clearInterval(timer);
    };
  }, [load, enabled]);
  const selectMax = () => {
    const edit = ++amountEdit.current;
    ++generation.current;
    request.current?.abort();
    request.current = null;
    if (editTimer.current) clearTimeout(editTimer.current);
    const known =
      lastQuote.current?.key === key ? lastQuote.current.quote : null;
    if (known) setAmount(formatSolanaTokenAmount(known.max, token.decimals));
    setState((previous) => ({
      key,
      quote: previous?.key === key ? previous.quote : null,
      loading: enabled,
      error: null,
    }));
    if (enabled)
      editTimer.current = setTimeout(async () => {
        const quote = await load();
        if (quote && edit === amountEdit.current)
          setAmount(formatSolanaTokenAmount(quote.max, token.decimals));
      }, AMOUNT_QUOTE_DEBOUNCE_MS);
  };
  return {
    quote: state?.key === key ? state.quote : null,
    loading: enabled && (state?.key !== key || state.loading),
    error: state?.key === key ? state.error : null,
    changeAmount: (value: string) => {
      ++amountEdit.current;
      ++generation.current;
      request.current?.abort();
      request.current = null;
      if (editTimer.current) clearTimeout(editTimer.current);
      setState((previous) => ({
        key,
        quote: previous?.key === key ? previous.quote : null,
        loading: enabled && !!value.trim(),
        error: null,
      }));
      setAmount(value);
      if (enabled && value.trim())
        editTimer.current = setTimeout(
          () => void load(),
          AMOUNT_QUOTE_DEBOUNCE_MS,
        );
    },
    acceptAmount: () => {
      ++amountEdit.current;
      ++generation.current;
      request.current?.abort();
      request.current = null;
      if (editTimer.current) clearTimeout(editTimer.current);
      setState((current) => current && { ...current, loading: false });
    },
    selectMax,
  };
}
